import type { Exercise, ExerciseEntry, Feel, GripVariant, SetLog } from '../../domain/types';
import { GRIP_VARIANTS } from '../../domain/types';
import type { Suggestion } from '../../domain/progression';
import { SetRow } from './SetRow';
import { FeelControl, AutoTextarea } from '../../ui/primitives';
import { IconSwap, IconPlus, IconTrash, IconWarn } from '../../ui/icons';
import { formatWeight } from '../../domain/weight';

export interface EntryHints {
  suggestion: Suggestion;
  beatThis: SetLog[];
  // Last session's note for this exercise, shown greyed as a reminder.
  prevNote?: string | null;
}

export function ExerciseCard({
  ex, entry, hints, open, onToggleOpen, onPatchEntry, onPatchSet, onToggleLogged, onAddSet, onRemoveSet, onSubstitute, onRemoveEntry, onMoveUp, onMoveDown, canMoveUp, canMoveDown,
}: {
  ex: Exercise;
  entry: ExerciseEntry;
  hints: EntryHints | undefined;
  open: boolean;
  onToggleOpen: () => void;
  onPatchEntry: (patch: Partial<ExerciseEntry>) => void;
  onPatchSet: (setIdx: number, patch: Partial<SetLog>) => void;
  onToggleLogged: (setIdx: number) => void;
  onAddSet: () => void;
  onRemoveSet: (setIdx: number) => void;
  onSubstitute: () => void;
  onRemoveEntry: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const working = entry.sets.filter((s) => !s.isWarmup);
  const loggedWorking = working.filter((s) => s.logged !== false).length;
  const allLogged = working.length > 0 && loggedWorking === working.length;
  const painFlag = entry.elbowFeel === 'pain' || entry.forearmFeel === 'pain';
  const stalled = hints?.suggestion?.stalled ?? false;

  const beat = hints?.beatThis ?? [];
  const beatLine = beat.length
    ? ex.timed
      ? beat.map((s) => `${s.reps}s`).join(' / ')
      : beat.map((s) => `${s.reps}x${formatWeight(s.weightKg, ex).replace(' kg', '').replace('/hand', '')}`).join(' / ')
    : null;

  return (
    <div className="card">
      <div className="spread" onClick={onToggleOpen} style={{ cursor: 'pointer' }}>
        <div className="grow">
          <div className="row wrap" style={{ gap: 6 }}>
            <strong style={{ fontSize: 16 }}>{ex.name}</strong>
            {entry.substitutedFrom && <span className="chip">sub</span>}
            {ex.neverToFailure && <span className="chip warn">no failure</span>}
            {/* Safety + done signals stay visible while collapsed. */}
            {!open && painFlag && <span className="chip bad">pain</span>}
            {!open && !painFlag && stalled && <span className="chip warn">stalled</span>}
            {!open && allLogged && <span className="chip good">done</span>}
          </div>
          <div className="faint" style={{ fontSize: 12 }}>
            {loggedWorking}/{working.length} sets logged
            {beatLine && <span> - beat {beatLine}</span>}
          </div>
        </div>
        <div className="row" style={{ gap: 4, alignItems: 'center' }}>
          {/* Reorder so the logged order matches what was actually done first. */}
          <button type="button" className="btn ghost sm" disabled={!canMoveUp} aria-label="move up"
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}>↑</button>
          <button type="button" className="btn ghost sm" disabled={!canMoveDown} aria-label="move down"
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}>↓</button>
          <span className={`chip ${open ? '' : 'accent'}`}>{open ? 'Hide' : 'Show'}</span>
        </div>
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          {hints?.suggestion && hints.suggestion.kind !== 'no_history' && (
            <div className="suggestion" style={{ marginBottom: 8 }}>{hints.suggestion.message}</div>
          )}
          {stalled && (
            <div className="warn-banner">
              <span className="row" style={{ gap: 6 }}><IconWarn width={16} height={16} /> {hints?.suggestion?.stallMessage}</span>
            </div>
          )}
          {painFlag && (
            <div className="warn-banner">
              <span className="row" style={{ gap: 6 }}><IconWarn width={16} height={16} /> Pain logged on this exercise. Consider backing off the load.</span>
            </div>
          )}
          {entry.sets.map((set, i) => (
            <SetRow
              key={i}
              set={set}
              ex={ex}
              index={entry.sets.slice(0, i).filter((s) => !s.isWarmup).length}
              beatReps={!set.isWarmup ? beat[entry.sets.slice(0, i).filter((s) => !s.isWarmup).length]?.reps ?? null : null}
              onChange={(patch) => onPatchSet(i, patch)}
              onToggleLogged={() => onToggleLogged(i)}
              onRemove={() => onRemoveSet(i)}
              canRemove={entry.sets.length > 1}
            />
          ))}

          <div className="row wrap" style={{ gap: 8, marginTop: 4 }}>
            <button className="btn ghost sm" onClick={onAddSet}><IconPlus width={16} height={16} /> Set</button>
            <button className="btn ghost sm" onClick={onSubstitute}><IconSwap width={16} height={16} /> Substitute</button>
            <button className="btn ghost sm" onClick={onRemoveEntry}><IconTrash width={16} height={16} /> Remove</button>
          </div>

          {(ex.tracksElbowFeel || ex.tracksForearmFeel) && <hr className="hr" />}
          {ex.tracksElbowFeel && (
            <FeelControl label="Elbow feel" value={entry.elbowFeel} onChange={(v: Feel) => onPatchEntry({ elbowFeel: v })} />
          )}
          {ex.tracksForearmFeel && (
            <FeelControl label="Forearm feel" value={entry.forearmFeel} onChange={(v: Feel) => onPatchEntry({ forearmFeel: v })} />
          )}
          {ex.tracksGrip && (
            <div className="field" style={{ marginBottom: 8 }}>
              <label>Grip variant</label>
              <div className="pill-list">
                {GRIP_VARIANTS.map((g) => (
                  <button
                    key={g}
                    className={`chip ${entry.gripVariant === g ? 'accent' : ''}`}
                    onClick={() => onPatchEntry({ gripVariant: entry.gripVariant === g ? null : (g as GripVariant) })}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hints?.prevNote && hints.prevNote.trim() && (
            <div className="faint" style={{ fontSize: 12, marginTop: 8, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
              Last time: {hints.prevNote.trim()}
            </div>
          )}
          <AutoTextarea
            placeholder="Note (form cue, swap reason)"
            value={entry.note}
            onChange={(note) => onPatchEntry({ note })}
            style={{ marginTop: 8 }}
          />
        </div>
      )}
    </div>
  );
}
